import React, { useState, useEffect } from 'react';

// Mapeamento de sigla UF para código IBGE
const UF_TO_CODIGO = {
  'RO': 11, 'AC': 12, 'AM': 13, 'RR': 14, 'PA': 15, 'AP': 16, 'TO': 17,
  'MA': 21, 'PI': 22, 'CE': 23, 'RN': 24, 'PB': 25, 'PE': 26, 'AL': 27, 'SE': 28, 'BA': 29,
  'MG': 31, 'ES': 32, 'RJ': 33, 'SP': 35,
  'PR': 41, 'SC': 42, 'RS': 43,
  'MS': 50, 'MT': 51, 'GO': 52, 'DF': 53
};

// Função utilitária para buscar municípios do arquivo JSON (apenas uma definição)
async function fetchMunicipios() {
  try {
    const resp = await fetch('/municipiosIBGE.json');
    if (!resp.ok) return [];
    const data = await resp.json();
    return data;
  } catch {
    return [];
  }
}

function normalizarConteudoCSV(conteudo = '') {
  let texto = String(conteudo).replace(/^\s+/, '');
  texto = texto.replace(/([^\r])\n/g, '$1\r\n');
  texto = texto.replace(/(\r\n)+$/g, '');
  return texto + '\r\n';
}

function criarBlobCSV(conteudo = '') {
  const BOM = '\uFEFF';
  return new Blob([BOM + normalizarConteudoCSV(conteudo)], { type: 'text/csv;charset=utf-8;' });
}

function normalizarCnpj(valor = '') {
  return String(valor || '').replace(/\D/g, '');
}

function obterClienteSelecionadoCnpj() {
  try {
    const salvo = localStorage.getItem('clienteSelecionado');
    if (!salvo) return '';
    const obj = JSON.parse(salvo);
    return normalizarCnpj(obj?.cnpj || '');
  } catch {
    return '';
  }
}

function getHistoricoKeyByCnpj(cnpj = '') {
  const cnpjLimpo = normalizarCnpj(cnpj);
  return cnpjLimpo ? `historicoCsvEstacoes_${cnpjLimpo}` : null;
}

function formatarDecimalCoordenada(valorNumerico) {
  if (!Number.isFinite(valorNumerico)) return '';
  return valorNumerico.toFixed(6).replace('.', ',');
}

function converterCoordenadaParaDecimal(valor, eixo = 'lat') {
  const textoOriginal = String(valor || '').trim();
  if (!textoOriginal) return '';

  // Já decimal (aceita vírgula ou ponto)
  const decimalDireto = textoOriginal.replace(',', '.');
  if (/^-?\d+(\.\d+)?$/.test(decimalDireto)) {
    const numero = Number(decimalDireto);
    if (!Number.isFinite(numero)) return textoOriginal;
    return formatarDecimalCoordenada(numero);
  }

  // DMS: extrai graus, minutos e segundos (na ordem em que aparecerem)
  const partesNumericas = textoOriginal.match(/\d+(?:[.,]\d+)?/g);
  if (!partesNumericas || partesNumericas.length === 0) return textoOriginal;

  const graus = Number(String(partesNumericas[0]).replace(',', '.')) || 0;
  const minutos = Number(String(partesNumericas[1] || '0').replace(',', '.')) || 0;
  const segundos = Number(String(partesNumericas[2] || '0').replace(',', '.')) || 0;

  let decimal = Math.abs(graus) + (minutos / 60) + (segundos / 3600);

  const textoMaiusculo = textoOriginal.toUpperCase();
  const temSinalNegativo = /^\s*-/.test(textoOriginal);
  const hemisferioSul = /\bS\b/.test(textoMaiusculo);
  const hemisferioOeste = /\bW\b|\bO\b/.test(textoMaiusculo);

  if (temSinalNegativo) {
    decimal = -decimal;
  } else if (eixo === 'lat' && hemisferioSul) {
    decimal = -decimal;
  } else if (eixo === 'long' && hemisferioOeste) {
    decimal = -decimal;
  }

  return formatarDecimalCoordenada(decimal);
}

function normalizarSimNao(valor = '') {
  const texto = String(valor || '').trim().toUpperCase();
  if (texto === 'S' || texto === 'N') return texto;
  return '';
}

function mapearLinhaEstacaoParaCsv(est = [], cnpj = '') {
  const snAberturaAtual = normalizarSimNao(est[8]);
  const snAberturaLegado = normalizarSimNao(est[9]);
  const usaLayoutLegado = !snAberturaAtual && !!snAberturaLegado;

  // layout atual: endereço[7], SN[8..12]
  // layout legado: endereço[8], SN_ABERTURA[9], TP[10], PROPRIO[11], CONTR[12], SAT[13]
  const enderecoRaw = usaLayoutLegado ? est[8] : est[7];
  const enderecoCompleto = enderecoRaw ? String(enderecoRaw).replace(/;/g, ',') : '';

  const snAbertura = usaLayoutLegado ? snAberturaLegado : snAberturaAtual;
  const tpEstacao = usaLayoutLegado ? (est[10] || '') : (est[9] || '');
  const snEnlaceProprio = normalizarSimNao(usaLayoutLegado ? est[11] : est[10]);
  const snEnlaceContratado = normalizarSimNao(usaLayoutLegado ? est[12] : est[11]);
  const snEnlaceSatelite = normalizarSimNao(usaLayoutLegado ? est[13] : est[12]);

  return [
    cnpj,
    est[0] || '', // ANO
    est[1] || '', // ID_ESTACAO (Nome da Estação)
    est[2] || '', // NU_ESTACAO_BDTA_ANATEL (Número da estação)
    converterCoordenadaParaDecimal(est[3], 'lat') || '', // LAT
    converterCoordenadaParaDecimal(est[4], 'long') || '', // LONG
    est[6] || '', // CO_MUNICIPIO_IBGE
    enderecoCompleto, // ENDERECO
    snAbertura, // SN_ABERTURA
    tpEstacao, // TP_ESTACAO
    snEnlaceProprio, // SN_ENLACE_PROPRIO
    snEnlaceContratado, // SN_ENLACE_CONTRATADO
    snEnlaceSatelite // SN_ENLACE_SATELITE
  ];
}

const campos = [
  'Ano',
  'Nome da Estação',
  'Número da estação',
  'Latitude',
  'Longitude',
  'UF',
  'Município',
  'Endereço', // Será substituído por subcampos
  'SN_ABERTURA',
  'TP_ESTACAO',
  'SN_ENLACE_PROPRIO',
  'SN_ENLACE_CONTRATADO',
  'SN_ENLACE_SATELITE'
];

export default function EstacoesInfra() {
  const [valores, setValores] = useState(Array(campos.length).fill(''));
  const [municipios, setMunicipios] = useState([]);
  const [clienteCnpj, setClienteCnpj] = useState(() => obterClienteSelecionadoCnpj());
  // Subcampos do endereço
  const [endereco, setEndereco] = useState({
    rua: '',
    numero: '',
    bairro: '',
    municipio: '',
    estado: '',
    cep: ''
  });
  // Lista de estações cadastradas
  const [estacoes, setEstacoes] = useState([]);
  const historicoKey = getHistoricoKeyByCnpj(clienteCnpj);

  // Histórico de CSVs gerados (por cliente)
  const [csvHistorico, setCsvHistorico] = useState(() => {
    if (!historicoKey) return [];
    try {
      const salvo = localStorage.getItem(historicoKey);
      return salvo ? JSON.parse(salvo) : [];
    } catch {
      return [];
    }
  });

  // Carregar histórico do localStorage sempre que o cliente mudar ou o localStorage for alterado (em outras abas)
  useEffect(() => {
    function atualizarClienteSelecionado() {
      setClienteCnpj(obterClienteSelecionadoCnpj());
    }

    atualizarClienteSelecionado();
    window.addEventListener('storage', atualizarClienteSelecionado);
    window.addEventListener('focus', atualizarClienteSelecionado);

    return () => {
      window.removeEventListener('storage', atualizarClienteSelecionado);
      window.removeEventListener('focus', atualizarClienteSelecionado);
    };
  }, []);

  useEffect(() => {
    function atualizarHistorico() {
      if (!historicoKey) {
        setCsvHistorico([]);
        return;
      }

      try {
        const salvo = localStorage.getItem(historicoKey);
        setCsvHistorico(salvo ? JSON.parse(salvo) : []);
      } catch {
        setCsvHistorico([]);
      }
    }
    atualizarHistorico();
    window.addEventListener('storage', atualizarHistorico);
    return () => window.removeEventListener('storage', atualizarHistorico);
  }, [historicoKey]);

  useEffect(() => {
    fetchMunicipios().then(setMunicipios);
    // eslint-disable-next-line
  }, []);

  const handleChange = (idx, value) => {
    setValores(v => {
      const novo = [...v];
      // Se for o campo Endereço (posição 7)
      if (idx === 7) {
        novo[7] = value; // Não usado diretamente, mas mantido para compatibilidade
      } else {
        novo[idx] = value;
      }
      return novo;
    });
  };

  // Atualiza subcampos do endereço e concatena na ordem solicitada, mostrando nome do município e UF
  const handleEnderecoChange = (campo, valor) => {
    setEndereco(e => {
      const novo = { ...e, [campo]: valor };
      // Ordem: Rua, Número, Bairro, Municipio, Estado, CEP (todos do bloco Endereço)
      const concat = `${novo.rua}, ${novo.numero}, ${novo.bairro}, ${novo.municipio}, ${novo.estado}, ${novo.cep}`;
      setValores(v => {
        const arr = [...v];
        arr[7] = concat;
        return arr;
      });
      return novo;
    });
  };

  const handleSubmit = e => {
    e.preventDefault();
    const valoresNormalizados = [...valores];
    // Índices: 3 = Latitude, 4 = Longitude
    valoresNormalizados[3] = converterCoordenadaParaDecimal(valoresNormalizados[3], 'lat');
    valoresNormalizados[4] = converterCoordenadaParaDecimal(valoresNormalizados[4], 'long');

    setEstacoes(estacoesAntigas => [...estacoesAntigas, valoresNormalizados]);
    setValores(Array(campos.length).fill(''));
    setEndereco({
      rua: '',
      numero: '',
      bairro: '',
      municipio: '',
      estado: '',
      cep: ''
    });
  };

  return (
    <>
      <form onSubmit={handleSubmit} style={{maxWidth:500, margin:'0 auto', background:'#fff', padding:24, borderRadius:8, boxShadow:'0 2px 8px #0001'}}>
        <h2 style={{marginBottom:24}}>Cadastro de Estações</h2>
        {campos.map((campo, idx) => (
          campo === 'Endereço' ? (
            <div key={campo} style={{marginBottom:16}}>
              <label style={{
                display:'block',
                fontWeight:700,
                marginBottom:4
              }}>Endereço</label>
              <div style={{display:'flex', gap:8, flexWrap:'wrap'}}>
                <input
                  type="text"
                  value={endereco.rua}
                  onChange={e => handleEnderecoChange('rua', e.target.value)}
                  placeholder="Rua"
                  style={{flex:2, minWidth:120, padding:8, borderRadius:4, border:'1px solid #ccc'}}
                />
                <input
                  type="text"
                  value={endereco.numero}
                  onChange={e => handleEnderecoChange('numero', e.target.value)}
                  placeholder="Número"
                  style={{flex:1, minWidth:60, padding:8, borderRadius:4, border:'1px solid #ccc'}}
                />
                <input
                  type="text"
                  value={endereco.bairro}
                  onChange={e => handleEnderecoChange('bairro', e.target.value)}
                  placeholder="Bairro"
                  style={{flex:1.5, minWidth:100, padding:8, borderRadius:4, border:'1px solid #ccc'}}
                />
                <input
                  type="text"
                  value={endereco.municipio}
                  onChange={e => handleEnderecoChange('municipio', e.target.value)}
                  placeholder="Município"
                  style={{flex:1.5, minWidth:100, padding:8, borderRadius:4, border:'1px solid #ccc'}}
                />
                <input
                  type="text"
                  value={endereco.estado}
                  onChange={e => handleEnderecoChange('estado', e.target.value)}
                  placeholder="Estado"
                  style={{flex:1, minWidth:60, padding:8, borderRadius:4, border:'1px solid #ccc'}}
                />
                <input
                  type="text"
                  value={endereco.cep}
                  onChange={e => handleEnderecoChange('cep', e.target.value)}
                  placeholder="CEP"
                  style={{flex:1, minWidth:80, padding:8, borderRadius:4, border:'1px solid #ccc'}}
                />
              </div>
              <div style={{fontSize:12, color:'#888', marginTop:4}}>
                {(endereco.rua || endereco.numero || endereco.bairro || endereco.municipio || endereco.estado || endereco.cep)
                  ? `Endereço completo: ${endereco.rua}, ${endereco.numero}, ${endereco.bairro}, ${endereco.municipio}, ${endereco.estado}, ${endereco.cep}`
                  : ''}
              </div>
            </div>
          ) : campo === 'SN_ABERTURA' ? (
            <div key={campo} style={{marginBottom:16}}>
              <label style={{
                display:'block',
                fontWeight:700,
                marginBottom:4
              }}>SN_ABERTURA</label>
              <select
                value={valores[idx]}
                onChange={e => handleChange(idx, e.target.value)}
                style={{
                  width:'100%',
                  padding:8,
                  borderRadius:4,
                  border:'1px solid #ccc',
                  fontWeight:400,
                  fontSize:15,
                  background:'#fff',
                  color: valores[idx] ? '#222' : '#888',
                  fontStyle: valores[idx] ? 'normal' : 'italic'
                }}
              >
                <option value="" disabled>Tem saída de tráfego/clientes nesta estação?</option>
                <option value="S">S</option>
                <option value="N">N</option>
              </select>
            </div>
          ) : campo === 'TP_ESTACAO' ? (
            <div key={campo} style={{marginBottom:16}}>
              <label style={{
                display:'block',
                fontWeight:700,
                marginBottom:4
              }}>TP_ESTACAO</label>
              <select
                value={valores[idx]}
                onChange={e => handleChange(idx, e.target.value)}
                style={{
                  width:'100%',
                  padding:8,
                  borderRadius:4,
                  border:'1px solid #ccc',
                  fontWeight:400,
                  fontSize:15,
                  background:'#fff',
                  color: valores[idx] ? '#222' : '#888',
                  fontStyle: valores[idx] ? 'normal' : 'italic'
                }}
              >
                <option value="" disabled>Qual a natureza da estação?</option>
                <option value="propria">propria</option>
                <option value="terceiro">terceiro</option>
                <option value="nuvem">nuvem</option>
                <option value="virtual">virtual</option>
              </select>
            </div>
          ) : campo === 'SN_ENLACE_PROPRIO' ? (
            <div key={campo} style={{marginBottom:16}}>
              <label style={{
                display:'block',
                fontWeight:700,
                marginBottom:4
              }}>SN_ENLACE_PROPRIO</label>
              <select
                value={valores[idx]}
                onChange={e => handleChange(idx, e.target.value)}
                style={{
                  width:'100%',
                  padding:8,
                  borderRadius:4,
                  border:'1px solid #ccc',
                  fontWeight:400,
                  fontSize:15,
                  background:'#fff',
                  color: valores[idx] ? '#222' : '#888',
                  fontStyle: valores[idx] ? 'normal' : 'italic'
                }}
              >
                <option value="" disabled>Possui enlace próprio interligando ela?</option>
                <option value="S">S</option>
                <option value="N">N</option>
              </select>
            </div>
          ) : campo === 'SN_ENLACE_CONTRATADO' ? (
            <div key={campo} style={{marginBottom:16}}>
              <label style={{
                display:'block',
                fontWeight:700,
                marginBottom:4
              }}>SN_ENLACE_CONTRATADO</label>
              <select
                value={valores[idx]}
                onChange={e => handleChange(idx, e.target.value)}
                style={{
                  width:'100%',
                  padding:8,
                  borderRadius:4,
                  border:'1px solid #ccc',
                  fontWeight:400,
                  fontSize:15,
                  background:'#fff',
                  color: valores[idx] ? '#222' : '#888',
                  fontStyle: valores[idx] ? 'normal' : 'italic'
                }}
              >
                <option value="" disabled>Possui enlace de terceiros interligando ela?</option>
                <option value="S">S</option>
                <option value="N">N</option>
              </select>
            </div>
          ) : campo === 'SN_ENLACE_SATELITE' ? (
            <div key={campo} style={{marginBottom:16}}>
              <label style={{
                display:'block',
                fontWeight:700,
                marginBottom:4
              }}>SN_ENLACE_SATELITE</label>
              <select
                value={valores[idx]}
                onChange={e => handleChange(idx, e.target.value)}
                style={{
                  width:'100%',
                  padding:8,
                  borderRadius:4,
                  border:'1px solid #ccc',
                  fontWeight:400,
                  fontSize:15,
                  background:'#fff',
                  color: valores[idx] ? '#222' : '#888',
                  fontStyle: valores[idx] ? 'normal' : 'italic'
                }}
              >
                <option value="" disabled>Possui conexão via satélite?</option>
                <option value="S">S</option>
                <option value="N">N</option>
              </select>
            </div>
          ) : (
            <div key={campo} style={{marginBottom:16}}>
              <label style={{
                display:'block',
                fontWeight: (idx === 0 || campo === 'Nome da Estação' || campo === 'Número da estação' || campo === 'Latitude' || campo === 'Longitude' || campo === 'UF' || campo === 'Município' || campo === 'Endereço') ? 700 : 500,
                marginBottom:4,
                color: idx === 0 ? '#111' : undefined
              }}>{campo}</label>
              {idx === 0 ? (
                <select
                  value={valores[0]}
                  onChange={e => handleChange(0, e.target.value)}
                  style={{
                    width:'100%',
                    padding:8,
                    borderRadius:4,
                    border:'1px solid #ccc',
                    fontWeight:400,
                    fontSize:16,
                    background:'#f3f6fa',
                    color:'#222',
                    boxShadow:'none',
                    letterSpacing:0
                  }}
                >
                  <option value="">Selecione o ano</option>
                  <option value="2024">2024</option>
                  <option value="2025">2025</option>
                  <option value="2026">2026</option>
                </select>
              ) : campo === 'UF' ? (
                <select
                  value={valores[idx]}
                  onChange={e => handleChange(idx, e.target.value)}
                  style={{
                    width:'100%',
                    padding:8,
                    borderRadius:4,
                    border:'1px solid #ccc',
                    fontWeight:400,
                    fontSize:15,
                    background:'#fff',
                    color:'#222'
                  }}
                >
                  <option value="">Selecione o estado</option>
                  <option value="AC">Acre (AC)</option>
                  <option value="AL">Alagoas (AL)</option>
                  <option value="AP">Amapá (AP)</option>
                  <option value="AM">Amazonas (AM)</option>
                  <option value="BA">Bahia (BA)</option>
                  <option value="CE">Ceará (CE)</option>
                  <option value="DF">Distrito Federal (DF)</option>
                  <option value="ES">Espírito Santo (ES)</option>
                  <option value="GO">Goiás (GO)</option>
                  <option value="MA">Maranhão (MA)</option>
                  <option value="MT">Mato Grosso (MT)</option>
                  <option value="MS">Mato Grosso do Sul (MS)</option>
                  <option value="MG">Minas Gerais (MG)</option>
                  <option value="PA">Pará (PA)</option>
                  <option value="PB">Paraíba (PB)</option>
                  <option value="PR">Paraná (PR)</option>
                  <option value="PE">Pernambuco (PE)</option>
                  <option value="PI">Piauí (PI)</option>
                  <option value="RJ">Rio de Janeiro (RJ)</option>
                  <option value="RN">Rio Grande do Norte (RN)</option>
                  <option value="RS">Rio Grande do Sul (RS)</option>
                  <option value="RO">Rondônia (RO)</option>
                  <option value="RR">Roraima (RR)</option>
                  <option value="SC">Santa Catarina (SC)</option>
                  <option value="SP">São Paulo (SP)</option>
                  <option value="SE">Sergipe (SE)</option>
                  <option value="TO">Tocantins (TO)</option>
                </select>
              ) : campo === 'Município' ? (
                <>
                  <input
                    list="municipios-ibge"
                    value={valores[idx]}
                    onChange={e => handleChange(idx, e.target.value)}
                    placeholder={valores[5] ? "Digite ou selecione o município" : "Escolha o estado primeiro"}
                    style={{
                      width:'100%',
                      padding:8,
                      borderRadius:4,
                      border:'1px solid #ccc',
                      fontWeight:400,
                      fontSize:15,
                      background:'#fff',
                      color:'#222'
                    }}
                    disabled={!valores[5]}
                    autoComplete="off"
                  />
                  <datalist id="municipios-ibge">
                    {(() => {
                      const codigoUF = UF_TO_CODIGO[valores[5]];
                      return municipios.filter(m => m.codigo_uf === codigoUF).map(m => (
                        <option key={m.codigo_ibge || m.nome} value={m.codigo_ibge}>
                          {m.nome}
                        </option>
                      ));
                    })()}
                  </datalist>
                </>
              ) : (
                <input
                  type="text"
                  value={valores[idx]}
                  onChange={e => handleChange(idx, e.target.value)}
                  placeholder={campo === 'Nome da Estação' ? 'Nome que você dá a sua estação' : campo === 'Número da estação' ? 'Número da estação no sistema da Anatel.' : campo === 'Latitude' && idx === 3 ? 'Latitude em graus decimais. Ex.: -23,5505' : campo === 'Longitude' && idx === 4 ? 'Longitude em graus decimais. Ex.: -46,6333' : ''}
                  style={{
                    width:'100%',
                    padding:8,
                    borderRadius:4,
                    border:'1px solid #ccc',
                    fontWeight:400,
                    fontSize:15,
                    background:'#fff',
                    color:'#222',
                    fontStyle: ((campo === 'Nome da Estação' || campo === 'Número da estação' || (campo === 'Latitude' && idx === 3) || (campo === 'Longitude' && idx === 4)) && !valores[idx]) ? 'italic' : 'normal'
                  }}
                />
              )}
            </div>
          )
        ))}
        <button type="submit" style={{marginTop:16, padding:'10px 24px', background:'#1976d2', color:'#fff', border:'none', borderRadius:4, fontWeight:600, fontSize:16, cursor:'pointer'}}>Salvar</button>
      </form>
      {/* Lista de estações cadastradas */}
      {estacoes.length > 0 && (
        <div style={{maxWidth:900, margin:'32px auto 0', background:'#fff', padding:24, borderRadius:8, boxShadow:'0 2px 8px #0001'}}>
          <h3 style={{marginBottom:16}}>Estações cadastradas</h3>
          <table style={{width:'100%', borderCollapse:'collapse'}}>
            <thead>
              <tr>
                {campos.map((campo, idx) => (
                  <th key={idx} style={{borderBottom:'1px solid #ccc', padding:'8px 4px', textAlign:'left', fontWeight:700, fontSize:15}}>{campo}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {estacoes.map((est, i) => (
                <tr key={i} style={{background: i%2 ? '#f8f9fb' : '#fff'}}>
                  {est.map((valor, j) => (
                    <td key={j} style={{padding:'8px 4px', fontSize:15}}>{valor}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <button onClick={() => {
            if (!clienteCnpj) {
              alert('Selecione um cliente antes de gerar o CSV.');
              return;
            }

            // Cabeçalho igual ao EX_ESTACOES.csv
            const header = [
              'CNPJ',
              'ANO',
              'ID_ESTACAO',
              'NU_ESTACAO_BDTA_ANATEL',
              'LAT',
              'LONG',
              'CO_MUNICIPIO_IBGE',
              'ENDERECO',
              'SN_ABERTURA',
              'TP_ESTACAO',
              'SN_ENLACE_PROPRIO',
              'SN_ENLACE_CONTRATADO',
              'SN_ENLACE_SATELITE'
            ];
            // Recupera o CNPJ do cliente do localStorage
            const cnpj = clienteCnpj;
            // Mapear os dados para a ordem correta
            const csvRows = [
              header.join(';'),
              ...estacoes.map(est => mapearLinhaEstacaoParaCsv(est, cnpj).join(';'))
            ];
            // Usa CRLF como quebra de linha e garante CRLF ao final
            const csvContent = normalizarConteudoCSV(csvRows.join('\r\n'));
            // Salvar histórico no localStorage (NÃO FAZ DOWNLOAD DIRETO)
            const data = new Date();
            // Recuperar razão social do cliente selecionado
            let razaoSocial = '';
            try {
              const salvo = localStorage.getItem('clienteSelecionado');
              if (salvo) {
                const obj = JSON.parse(salvo);
                razaoSocial = (obj.razaoSocial || '').replace(/[^a-zA-Z0-9]/g, '_');
              }
            } catch {}
            const ano = data.getFullYear();
            const mes = String(data.getMonth() + 1).padStart(2, '0');
            const nomeArquivo = `ESTACOES_${razaoSocial}_${ano}_${mes}.csv`;
            const historicoItem = {
              nome: nomeArquivo,
                conteudo: csvContent,
              data: data.toLocaleString()
            };
            let novoHistorico = [historicoItem, ...csvHistorico];
            // Limitar histórico a 10 arquivos
            if (novoHistorico.length > 10) novoHistorico = novoHistorico.slice(0, 10);
            setCsvHistorico(novoHistorico);
            if (historicoKey) {
              localStorage.setItem(historicoKey, JSON.stringify(novoHistorico));
            }
          }}
            style={{marginTop:24, padding:'10px 24px', background:'#388e3c', color:'#fff', border:'none', borderRadius:4, fontWeight:700, fontSize:16, cursor:'pointer'}}
          >Gerar CSV</button>
        </div>
      )}
      {/* Histórico de CSVs gerados */}
      {csvHistorico.length > 0 && (
        <div style={{marginTop:32}}>
          <h4 style={{marginBottom:8}}>Histórico de CSVs gerados</h4>
          <ul style={{paddingLeft:0, listStyle:'none'}}>
            {csvHistorico.map((item, idx) => (
              <li key={idx} style={{marginBottom:6, fontSize:15, display:'flex', alignItems:'center', gap:12}}>
                <span style={{fontWeight:500}}>{item.nome} <span style={{color:'#888', fontSize:13}}>({item.data})</span></span>
                <button
                  style={{background:'#1976d2', color:'#fff', border:'none', borderRadius:3, padding:'2px 10px', fontSize:13, cursor:'pointer', marginLeft:4}}
                  title="Baixar este CSV"
                  onClick={() => {
                    const blob = criarBlobCSV(item.conteudo);
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', item.nome);
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                  }}
                >Baixar</button>
                <button
                  style={{background:'#e53935', color:'#fff', border:'none', borderRadius:3, padding:'2px 10px', fontSize:13, cursor:'pointer'}}
                  title="Excluir este CSV do histórico"
                  onClick={() => {
                    if (window.confirm('Tem certeza que deseja excluir este arquivo do histórico?')) {
                      const novoHistorico = csvHistorico.filter((_, i) => i !== idx);
                      setCsvHistorico(novoHistorico);
                      if (historicoKey) {
                        localStorage.setItem(historicoKey, JSON.stringify(novoHistorico));
                      }
                    }
                  }}
                >Excluir</button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}
